// Global
import { RichText, RichTextField } from '@sitecore-content-sdk/nextjs';
import { RichTextProps } from '@sitecore-content-sdk/nextjs';
import React from 'react';

// Local
import useIsEditing from 'lib/hooks/sitecore/useIsEditing';
import { useFieldWithFallbacks } from 'lib/hooks/sitecore/useFieldWithFallbacks';
import { ReplacementToken } from 'lib/utils/string-utils';

import {
  EnhancedRichTextOptionalProps,
  getRteClassNames,
  useEnhancedRichText,
} from 'components/helpers/Atoms/EnhancedRichText/EnhancedRichText';

interface RichTextWrapperProps extends RichTextProps, React.HTMLAttributes<HTMLDivElement> {
  fallbacks?: (RichTextField | undefined)[];
  tokens?: ReplacementToken[];
  options?: EnhancedRichTextOptionalProps;
}

/**
 * This hook is used to render a rich text field.
 * This is needed because a component cannot return null.
 * This needs to be a hook because we need to use the useIsEditing hook to determine if we are in edit mode
 * @param props - The props for the rich text wrapper.
 * @returns A JSX.Element if the field has a value, or if we are editing, otherwise null.
 */
export function useRichTextWrapper(props: RichTextWrapperProps): JSX.Element | null {
  const { field, fallbacks, tokens, editable = true } = props;
  const isEditing = useIsEditing() && editable;

  const { renderField } = useFieldWithFallbacks(field, fallbacks);

  const updatedField = useUpdatedRichTextField({
    field: renderField,
    tokens,
    editable,
    options: props.options,
  });

  const hasValue = !!updatedField?.value;

  // We should only render if it has a value, or if we are editing
  const shouldRender = hasValue || isEditing;
  if (!shouldRender) return null;
  return <RichTextWrapper {...props} />;
}

const RichTextWrapper = ({
  field,
  fallbacks,
  tokens,
  editable = true,
  className,
  options,
  ...props
}: RichTextWrapperProps): JSX.Element => {
  const isEditing = useIsEditing() && editable;

  const { renderField } = useFieldWithFallbacks(field, fallbacks);

  const updatedField = useUpdatedRichTextField({
    field: renderField,
    tokens,
    editable,
    options,
  });

  const hasValue = !!updatedField?.value;

  // We should only render if it has a value, or if we are editing
  const shouldRender = hasValue || isEditing;
  if (!shouldRender) return <></>;

  // In editing mode, move `className` to wrapping div,
  // because Sitecore doesn't render the props if field is empty,
  // and so the "rte" and other css classes are missing initially.
  if (isEditing) {
    return (
      <div className={getRteClassNames(className, options)}>
        <RichText
          {...props}
          data-component="helpers/fieldwrappers/richtextwrapper"
          editable={editable}
          field={updatedField}
        />
      </div>
    );
  }
  return (
    <RichText
      {...props}
      className={getRteClassNames(className, options)}
      data-component="helpers/fieldwrappers/richtextwrapper"
      editable={editable}
      field={updatedField}
      // The SDK's RichText hijacks clicks on any internal link (`a[href^="/"]`)
      // and does a client-side `router.push`. Our proxied download links are
      // rewritten to `/api/download?url=...` (with a `download` attribute), so
      // the router treats `/api/download` as a page route, resolves it against
      // the catch-all `[[...path]]`, and renders "page not found" on first click
      // (a hard refresh works because that hits the real API route). Excluding
      // `[download]` links keeps SPA nav for real pages while letting downloads
      // perform a native navigation to the API route.
      internalLinksSelector={'a[href^="/"]:not([download])'}
    />
  );
};

function useUpdatedRichTextField({
  field,
  tokens,
  editable = true,
  options,
}: RichTextWrapperProps) {
  const isEditing = useIsEditing() && editable;

  const updatedValue = useEnhancedRichText({
    value: field?.value || '',
    tokens,
    options: isEditing ? undefined : options,
  });

  const updatedField: RichTextField = {
    value: updatedValue,
  };

  return isEditing ? field : updatedField;
}

export default RichTextWrapper;
